import { useState } from 'react';
import NumberedList from 'src/components/numberedList';
import BulletedList from 'src/components/bulletedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">Why do I have high blood pressure?</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">6 tips to help reduce your blood pressure</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">How to set goals that really work</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">Latest BHF research</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 4: What causes high blood pressure?'}
      </h1>
      <p>As with any new diagnosis, understanding what has contributed to your high blood pressure is an important step in getting it under control.</p>
      <div className="visible-xs">{getMenu()}</div>
      <h2>Why do I have high blood pressure?</h2>
      <p>
        A lot of different things can lead to high blood pressure, so you need to focus on the ones you can control.
        There are very clear lifestyle choices that play a key role in your heart health.
      </p>
      <p>
        Take some time to keep track of what you eat, how much you move about and your less healthy habits.
        From there you can target what is affecting your blood pressure.
      </p>
      <img alt="High blood pressure risks" className="navigator__image-full" src={`${PROGRAM_URL}wk4-risk.jpg`} />

      <h2>6 tips to help reduce your blood pressure</h2>
      <p>{`There is lots of information out there on lowering your blood pressure and sometimes it's hard to know where to start. There's no trendy superfood or complicated gym workout that will change everything overnight. Instead, you need to make small changes in several areas of your life, and stick to them.`}</p>
      <p>6 areas for small changes:</p>
      <NumberedList items={[
        `Exercise for 150 minutes a week - that's 30 minutes a day`,
        'Make sure you stay a healthy weight',
        'Eat more fruit and veg',
        'Cut down on salt',
        'Avoid drinking more than 14 units of alcohol a week',
        'Always take your medication as prescribed',
      ]}
      />
      <p>Remember to build up these habits gradually. Changing one thing at a time will make it easier to stick to them.</p>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/research/blood-pressure/blood-pressure-tips" rel="noreferrer" target="_blank">
        Read 6 tips to start bringing your blood pressure down {'>'}
      </a>

      <h2>How to set goals that really work</h2>
      <p>{`Making gradual changes over time is the best way to make a real impact on your heart health. But to start, you need to set some goals that you can stick to. Try to focus on things that are:`}</p>
      <BulletedList items={[
        <span key="1"><strong>Achievable</strong>{` - for example 'eat more fruit and veg'`}</span>,
        <span key="2"><strong>Measureable</strong>{` - for example '5 portions of fruit and veg a day'`}</span>,
        <span key="3"><strong>Time-specific</strong>{` - for example, 'start today, see how I'm doing in 1 week'`}</span>]
      }
      />
      <p>{`While it's good to have an end goal, make sure you set smaller goals along the way. It will help you stay motivated.`}</p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/XpD-ri4HjPA"
        />
      </div>

      <h2>BHF Researchers uncover the causes of hypertension</h2>
      <p>{`Professor Rhian Touyz has been working at one of the BHF's Centres of Excellence to discover the causes of high blood pressure.`}</p>
      <p>{`“I have always been very interested in the biology of vessels in health and disease, and that's what the Centre of Excellence allows us to study,” she says.`}</p>
      <p>
        Prof Touyz and her team are focusing on one of the possible causes of damage to blood vessels from hypertension (high blood pressure).
        She hopes that by developing her groundbreaking treatment, we will be able to better treat the damage to blood vessels caused by high blood pressure.
      </p>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/research/causes-of-high-blood-pressure" rel="noreferrer" target="_blank">
        {`Read more about Prof Touyz's research >`}
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
